import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, ChevronLeft, ChevronRight } from 'lucide-react';

const serviceImages = [
  {
    id: 1,
    image: "https://images.pexels.com/photos/6755053/pexels-photo-6755053.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Reparación de Celulares",
    description: "Servicio técnico especializado en smartphones y tablets"
  },
  {
    id: 2,
    image: "https://images.pexels.com/photos/7639355/pexels-photo-7639355.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Reparación de Notebooks",
    description: "Diagnóstico y reparación de laptops de todas las marcas"
  },
  {
    id: 3,
    image: "https://images.pexels.com/photos/2582931/pexels-photo-2582931.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Reparación de PC de Escritorio",
    description: "Mantenimiento y reparación de computadoras de escritorio"
  },
  {
    id: 4,
    image: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Reparación de Televisores",
    description: "Servicio técnico para Smart TV LED, LCD y OLED"
  },
  {
    id: 5,
    image: "https://images.pexels.com/photos/3945683/pexels-photo-3945683.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Reparación de Drones",
    description: "Servicio especializado en drones y equipos aéreos"
  },
  {
    id: 6,
    image: "https://images.pexels.com/photos/3183164/pexels-photo-3183164.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Dispositivos de Realidad Virtual",
    description: "Reparación de visores VR y accesorios"
  },
  {
    id: 7,
    image: "https://images.pexels.com/photos/5727002/pexels-photo-5727002.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Impresoras de Tinta",
    description: "Reparación y mantenimiento de impresoras Epson, HP y más"
  },
  {
    id: 8,
    image: "https://images.pexels.com/photos/3825581/pexels-photo-3825581.jpeg?auto=compress&cs=tinysrgb&w=1200",
    title: "Impresoras 3D",
    description: "Servicio técnico para impresoras de fabricación aditiva"
  }
];

const TechServiceSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-advance carousel every 1.5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % serviceImages.length);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + serviceImages.length) % serviceImages.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % serviceImages.length);
  };

  const currentImage = serviceImages[currentIndex];

  return (
    <section className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600/20 border border-violet-500/30 rounded-full mb-6"
          >
            <Wrench className="w-4 h-4 text-violet-400" />
            <span className="text-violet-300 text-sm font-mono tracking-wider uppercase">Soporte Profesional</span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Servicio Técnico
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Servicio profesional y garantizado
          </motion.p>
        </div>

        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="relative max-w-4xl mx-auto"
        >
          {/* Main Image Container */}
          <div className="relative aspect-video rounded-2xl overflow-hidden bg-gray-900 shadow-2xl shadow-violet-500/20">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0"
              >
                <img
                  src={currentImage.image}
                  alt={currentImage.title}
                  className="w-full h-full object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              </motion.div>
            </AnimatePresence>

            {/* Text Overlay with Neon Effect */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                  className="text-center"
                >
                  <h3 
                    className="text-3xl lg:text-4xl font-bold text-white mb-3"
                    style={{
                      textShadow: '0 0 10px rgba(139, 92, 246, 0.8), 0 0 20px rgba(139, 92, 246, 0.6), 0 0 30px rgba(139, 92, 246, 0.4), 0 0 40px rgba(139, 92, 246, 0.2)'
                    }}
                  >
                    {currentImage.title}
                  </h3>
                  <p 
                    className="text-xl text-white/90 font-medium"
                    style={{
                      textShadow: '0 0 8px rgba(139, 92, 246, 0.6), 0 0 16px rgba(139, 92, 246, 0.4)'
                    }}
                  >
                    {currentImage.description}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm border border-violet-500/30 flex items-center justify-center text-white hover:bg-violet-600/50 hover:border-violet-500 transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm border border-violet-500/30 flex items-center justify-center text-white hover:bg-violet-600/50 hover:border-violet-500 transition-all"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {serviceImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'w-8 bg-violet-500 shadow-lg shadow-violet-500/50' 
                    : 'w-2 bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>

          {/* Service Counter */}
          <div className="text-center mt-4">
            <span className="text-violet-400 font-mono text-sm">
              {currentIndex + 1} / {serviceImages.length}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechServiceSection;
